import React from 'react';
import isEmpty from 'is-empty';
import makeStyles from '@mui/styles/makeStyles';

import {Card, Table, Tooltip} from 'spectra-logic-ui/components';
import StorageIcon from 'spectra-logic-ui/icons/Storage';

import {storageClassString} from '@/enum';
import {Aggregate, Pool} from '@/types';
import ButtonToolbar from '@/components/button_toolbar';
import CardHeader from '@/components/card_header';
import DeleteDialog from '@/components/delete_dialog';
import {StorageRowId} from '@/storage/types';
import CreateAggregateDialog from '@/storage/form/add/create_aggregate';
import EditAggregateDialog from '@/storage/form/edit_aggregate';

type Props = {
  aggregates: Aggregate[];
  pools: Pool[];
  selectedAggregate?: Aggregate;
  selectedRowId: StorageRowId;
  selectRowId: Function;
  clearSelectedRow: () => any;
  setOpenDetails: Function;
  fetching: boolean;
}

const useStyles = makeStyles({
  nowrap: {
    whiteSpace: 'nowrap',
  },
  storage: {
    overflow: 'hidden',
    whiteSpace: 'nowrap',
    maxWidth: 500,
  },
});

const storageListTooltip = 'This column displays a list of all storage in the group. ' +
  'Click the details button to see the full list if it is too long.';

const Aggregates = (props: Props) => {
  const {aggregates, pools, selectedAggregate, selectedRowId, selectRowId, clearSelectedRow, setOpenDetails,
    fetching} = props;
  const classes = useStyles();

  const selectedAggregateId = selectedRowId.aggregateId;

  return (
    <div>
      <Card>
        <CardHeader icon={StorageIcon}>Storage Groups</CardHeader>
        <Card.Body sx={{position: 'relative'}}>
          <ButtonToolbar sx={{position: 'sticky', top: 80}}>
            <ButtonToolbar.CreateDialogButton
              title='Create'
              dialog={(props: any) => <CreateAggregateDialog pools={pools} {...props} />}
              onClick={clearSelectedRow}
            />
            <ButtonToolbar.EditDialogButton
              disabled={isEmpty(selectedAggregate)}
              dialog={(props: any) => <EditAggregateDialog aggregate={selectedAggregate} pools={pools} {...props} />}
              onSuccess={clearSelectedRow}
            />
            <ButtonToolbar.DeleteDialogButton
              disabled={isEmpty(selectedAggregate)}
              dialog={(props: any) => (
                <DeleteDialog
                  id={selectedAggregateId} name={selectedAggregate ? selectedAggregate.name : ''}
                  title='Delete Storage Group' resource='aggregates' {...props}
                />
              )}
              onSuccess={clearSelectedRow}
            />
          </ButtonToolbar>
          <Table>
            <Table.Header>
              <Table.Row>
                <Table.Cell className={classes.nowrap}>Name</Table.Cell>
                <Table.Cell className={classes.nowrap}>Storage<Tooltip>{storageListTooltip}</Tooltip></Table.Cell>
                <Table.Cell className={classes.nowrap}>Storage Class</Table.Cell>
                <Table.Cell></Table.Cell>
              </Table.Row>
            </Table.Header>
            <Table.Body isLoading={fetching}>
              {aggregates.map((aggregate) => (
                <Table.Row key={aggregate.id} selected={selectedAggregateId === aggregate.id} hover
                  onClick={() => selectRowId({aggregateId: aggregate.id})}
                >
                  <Table.Cell>{aggregate.name}</Table.Cell>
                  <Table.Cell className={classes.storage}><AggregateStorageList aggregate={aggregate} /></Table.Cell>
                  <Table.Cell className={classes.nowrap}>
                    {storageClassString(aggregate.storageClass)}
                  </Table.Cell>
                  <Table.CellDetailsButton onClick={() => setOpenDetails(true)} />
                </Table.Row>
              ))}
            </Table.Body>
          </Table>
        </Card.Body>
      </Card>
    </div>
  );
};

const AggregateStorageList = ({aggregate}: {aggregate: Aggregate}) => {
  if (!aggregate.storage || aggregate.storage.length === 0) {
    return <span>--</span>;
  }
  const names = aggregate.storage.map((s) => `${s.name} (${s.id})`);
  return (
    <span>{names.join(', ')}</span>
  );
};

export default Aggregates;
